// ═══════════════════════════════════════════════════════════
// V·IANet Requests — Nueva Solicitud
// Selector de tipo + formulario dinamico + enviar
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { request } from '../../shared/services/api';
import { useI18n } from '../../../i18n';
import { FormSchemaRenderer, validateSchema } from './components/FormSchemaRenderer';
import type { FormComponent } from './components/FormSchemaRenderer';
import type {
  TipoSolicitud,
  GrupoSolicitud,
  FormularioSchema,
  SimpleResponse,
} from './types/requests.types';

interface Schema {
  components: FormComponent[];
}

export function RequestCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contextProcesoId = searchParams.get('procesoId');
  const contextActividadId = searchParams.get('actividadId');

  const [tipos, setTipos] = useState<TipoSolicitud[]>([]);
  const [grupos, setGrupos] = useState<GrupoSolicitud[]>([]);
  const [selectedTipo, setSelectedTipo] = useState<TipoSolicitud | null>(null);
  const [schema, setSchema] = useState<Schema | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Load tipos and grupos
  useEffect(() => {
    Promise.all([
      request<SimpleResponse<TipoSolicitud>>('/requests/tipos-solicitud'),
      request<SimpleResponse<GrupoSolicitud>>('/requests/grupos-solicitud'),
    ])
      .then(([t, g]) => {
        setTipos(t.data.filter((x) => x.activo));
        setGrupos(g.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load schema when tipo selected
  useEffect(() => {
    if (!selectedTipo) {
      setSchema(null);
      return;
    }
    request<SimpleResponse<FormularioSchema>>(
      `/requests/formularios-schema?tipoSolicitudId=${selectedTipo.id}`,
    )
      .then((res) => {
        const active = res.data.find((f) => f.activo);
        if (active?.schema) setSchema(active.schema as unknown as Schema);
      })
      .catch(() => {});
  }, [selectedTipo]);

  const handleChange = useCallback((key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  }, []);

  const handleSubmit = async (enviar: boolean) => {
    if (!selectedTipo || !schema) return;

    if (enviar) {
      const errs = validateSchema(schema, values);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await request<{ data: { id: string } }>('/requests/solicitudes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipoSolicitudId: selectedTipo.id,
          valores: values,
          ...(contextProcesoId ? { procesoId: contextProcesoId } : {}),
          ...(contextActividadId ? { actividadId: contextActividadId } : {}),
        }),
      });

      if (enviar) {
        await request<unknown>(`/requests/solicitudes/${res.data.id}/enviar`, { method: 'POST' });
        setToast(t.requests.enviadaOk);
      } else {
        setToast(t.requests.borradorGuardado);
      }

      setTimeout(() => navigate(`/requests/${res.data.id}`), 1200);
    } catch {
      setToast(t.requests.errorGenerico);
    } finally {
      setSubmitting(false);
    }
  };

  const grupoMap = new Map(grupos.map((g) => [g.id, g]));

  return (
    <div
      style={{ padding: 32, maxWidth: 800, margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}
    >
      <h1
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: '#1F3864',
          marginBottom: contextProcesoId ? 8 : 24,
        }}
      >
        {t.requests.nueva}
      </h1>

      {contextProcesoId && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 20,
            padding: '6px 12px',
            borderRadius: 6,
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            fontSize: 12,
            color: '#1E40AF',
          }}
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span style={{ fontWeight: 600 }}>
            {t.requests.desdeActividad ?? 'Desde actividad del flujograma'}
          </span>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            padding: '10px 20px',
            borderRadius: 8,
            background: '#1F3864',
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            zIndex: 999,
            animation: 'fadeIn 0.3s',
          }}
        >
          {toast}
        </div>
      )}

      {/* Step 1: select tipo */}
      {!selectedTipo && (
        <>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>
            {t.requests.seleccionarTipo}
          </p>
          {loading ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>{t.common.cargando}</div>
          ) : tipos.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic' }}>
              {t.requests.sinTipos}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240, 1fr))',
                gap: 16,
              }}
            >
              {tipos.map((tipo) => {
                const grupo = grupoMap.get(tipo.grupoId);
                return (
                  <button
                    key={tipo.id}
                    onClick={() => {
                      setSelectedTipo(tipo);
                      setValues({});
                      setErrors({});
                    }}
                    style={{
                      textAlign: 'left',
                      padding: 16,
                      borderRadius: 12,
                      border: '1px solid #E5E7EB',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#2F5496',
                          background: '#2F549610',
                          padding: '2px 8px',
                          borderRadius: 4,
                        }}
                      >
                        {tipo.codigo}
                      </span>
                      {grupo && (
                        <span style={{ fontSize: 10, color: '#9CA3AF' }}>{grupo.nombre}</span>
                      )}
                    </div>
                    <div
                      style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', marginBottom: 4 }}
                    >
                      {tipo.nombre}
                    </div>
                    {tipo.descripcion && (
                      <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>
                        {tipo.descripcion}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Step 2: form */}
      {selectedTipo && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <button
              onClick={() => {
                setSelectedTipo(null);
                setSchema(null);
                setValues({});
                setErrors({});
              }}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#2F5496',
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              &larr; {t.requests.cambiarTipo}
            </button>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 12,
                fontWeight: 700,
                color: '#2F5496',
                background: '#2F549610',
                padding: '3px 10px',
                borderRadius: 6,
              }}
            >
              {selectedTipo.codigo}
            </span>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {selectedTipo.nombre}
            </span>
          </div>

          {!schema ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>{t.common.cargando}</div>
          ) : (
            <>
              <div
                style={{
                  background: 'white',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                  padding: 24,
                  marginBottom: 24,
                }}
              >
                <FormSchemaRenderer
                  schema={schema}
                  values={values}
                  onChange={handleChange}
                  errors={errors}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={submitting}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: '10px 20px',
                    background: 'white',
                    color: '#374151',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  {t.requests.guardarBorrador}
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={submitting}
                  style={{
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #1F3864, #2F5496)',
                    color: 'white',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  {t.requests.enviarAprobacion}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
